import { Tbody } from '@chakra-ui/table';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, TableBodyPropGetter, TableBodyProps as ReactTableBodyProps } from 'react-table';
import { TableDataRow } from './TableDataRow';
import { TableLoader } from './TableLoader';
import { TableNoData } from './TableNoData';

// eslint-disable-next-line @typescript-eslint/ban-types
interface TableBodyProps<Data extends object> {
  rows: Row<Data>[];
  prepareRow: (row: Row<Data>) => void;
  getTableBodyProps: (propGetter?: TableBodyPropGetter<Data>) => ReactTableBodyProps;
  columnCount: number;
  isLoading: boolean;
  onRowClick?: (row: Data) => void;
  getRowBgColor?: (row: Data, index: number) => string | undefined;
  getCellBgColor?: (row: Data, columnId: string) => string | undefined;
  getCellProps?: (row: Data, columnId: string, index: number) => Record<string, unknown>;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function TableBody<Data extends object>({
  rows,
  prepareRow,
  getTableBodyProps,
  columnCount,
  isLoading,
  onRowClick,
  getRowBgColor,
  getCellBgColor,
  getCellProps,
}: TableBodyProps<Data>): JSX.Element {
  const { t } = useTranslation('common');

  const renderContent = () => {
    if (isLoading) {
      return <TableLoader columnCount={columnCount} />;
    }

    if (rows.length === 0) {
      return <TableNoData columnCount={columnCount} noDataText={t('noDataAvailable')} />;
    }

    return rows.map((row, index) => {
      prepareRow(row);
      return (
        <TableDataRow
          key={row.id}
          row={row}
          index={index}
          onRowClick={onRowClick}
          getRowBgColor={getRowBgColor}
          getCellBgColor={getCellBgColor}
          getCellProps={getCellProps}
        />
      );
    });
  };

  return <Tbody {...getTableBodyProps()}>{renderContent()}</Tbody>;
}

TableBody.defaultProps = {
  onRowClick: undefined,
  getRowBgColor: undefined,
  getCellBgColor: undefined,
  getCellProps: undefined,
};
